import React, { useState, useEffect, useRef } from 'react';
import {
  Form,
  Input,
  Button,
  Card,
  Row,
  Col,
  Checkbox,
  Typography,
  Divider,
  Space,
  Popconfirm,
  message,
  Modal,
  notification,
} from 'antd';
import _ from 'lodash';
import {
  DeleteOutlined,
  CopyOutlined,
  EditOutlined,
  PlusOutlined,
  SaveOutlined,
} from '@ant-design/icons';
import { observer } from 'mobx-react';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';
import { ShopStore, CurrentUserStore, InspectionsStore } from 'stores';
import { Logger } from 'stores/Classes';
import { DealershipChooser } from 'components';

import Group from './common/Group';
import FleetDetails from './common/FleetDetails';
import InitDetails from './common/InitDetails';
import { generateRandomId } from './common/utils';

const ResponsiveEditField = styled.div`
  width: 100%;
  margin: 0 auto;
  padding: 0 24px;
  .edit-template-inner {
    max-width: 1000px;
    margin: 0 auto;
    background: #f5f6fa;
    min-height: 100vh;
    padding: 32px;
    transition:
      max-width 0.2s,
      padding 0.2s;
  }
  @media (max-width: 1066px) {
    padding: 0;
    .edit-template-inner {
      max-width: 90vw;
      padding: 8px;
    }
    .ant-row {
      flex-direction: column;
    }
    .ant-col {
      width: 100%;
      max-width: 100%;
    }
    .checkbox-row,
    .checkbox-row.ant-row {
      flex-direction: row !important;
      flex-wrap: nowrap !important;
      overflow-x: auto !important;
    }
    .checkbox-row .ant-col,
    .checkbox-row.ant-row .ant-col {
      width: 25% !important;
      max-width: 25% !important;
    }
  }
`;

const { Title, Text, Link } = Typography;

const InspectionForm = () => {
  const [form] = Form.useForm();
  let history = useHistory();
  const [formTitle, setFormTitle] = useState('Inspection Form 1');

  const unblockRef = useRef(null);
  const [isDirty, setIsDirty] = useState(false);

  const [fleetAddress, setFleetAddress] = useState('');
  const [fleetLogoURL, setFleetLogoURL] = useState('');

  const [groups, setGroups] = useState([
    {
      id: Date.now(),
      title: 'Group 1',
      sections: [
        {
          id: Date.now() + 1,
          checkboxes: [false, false, false, false],
          description: '',
          freeText: '',
        },
      ],
    },
  ]);
  const [editable, setEditable] = useState(true);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const initialize = async () => {
      await loadData(ShopStore.currentShop.id);
    };

    initialize();
  }, []);

  useEffect(() => {
    loadData(ShopStore.currentShop.id);
  }, [ShopStore.currentShop.id]);

  useEffect(() => {
    if (!isDirty) return;

    unblockRef.current = history.block((location, action) => {
      Modal.confirm({
        title: 'Save Changes',
        content: 'Do you want to save changes before exiting?',
        okText: 'Save & Exit',
        cancelText: 'Exit Without Saving',
        onOk: async () => {
          unblockRef.current?.();
          await handleSave(false);
          history.push(location.pathname);
        },
        onCancel: () => {
          unblockRef.current?.();
          history.push(location.pathname);
        },
        okCancel: true,
        closable: true,
        maskClosable: true,
        cancelButtonProps: { danger: true },
      });

      return false;
    });

    return () => {
      unblockRef.current?.();
    };
  }, [isDirty, history]);

  const loadData = async (shopId) => {
    try {
      const shop = await ShopStore.getShopById(shopId);

      const address = _.get(shop, 'address', '');
      const logoUrl = _.get(shop, 'logoUrl', '');

      setFleetAddress(address);
      setFleetLogoURL(logoUrl);
    } catch (e) {
      Logger.log(e);
    }
  };

  const addGroup = () => {
    setIsDirty(true);
    setGroups([
      ...groups,
      {
        id: Date.now(),
        title: `Group ${groups.length + 1}`,
        sections: [
          {
            id: Date.now() + 1,
            checkboxes: [false, false, false, false],
            description: '',
            freeText: '',
          },
        ],
      },
    ]);
  };

  const deleteGroup = (idx) => {
    setIsDirty(true);
    setGroups(groups.filter((_, i) => i !== idx));
  };

  const duplicateGroup = (idx) => {
    setIsDirty(true);
    const group = groups[idx];
    const newGroup = {
      ...group,
      id: Date.now(),
      title: `${group.title} Copy`,
      sections: group.sections.map((sec) => ({
        ...sec,
        id: Date.now() + Math.random(),
      })),
    };
    setGroups([
      ...groups.slice(0, idx + 1),
      newGroup,
      ...groups.slice(idx + 1),
    ]);
  };

  const updateGroup = (idx, newGroup) => {
    setGroups(groups.map((g, i) => (i === idx ? newGroup : g)));
    setIsDirty(true);
  };

  const handleSave = async (stay = true) => {
    try {
      const shop_id = ShopStore.currentShop.id;
      const payload = {
        user_id: CurrentUserStore.user.id,
        title: formTitle,
        form_id: generateRandomId(),
        groups,
      };

      setIsDirty(false);

      await InspectionsStore.createTemplate(shop_id, payload);
      if (stay) {
        history.push('/inspection-forms', { tab: 'templates' });
      }

      notification.success({
        message: 'Success',
        description: 'Template created successfully',
      });
    } catch (e) {
      notification.error({
        message: 'Error',
        description: 'Please try create template later.',
      });
      console.log(e);
      return;
    }
  };

  const handleEdit = () => setEditable(true);

  return (
    <ResponsiveEditField>
      <div
        // style={{
        //   background: '#f5f6fa',
        //   minHeight: '100vh',
        //   padding: 32,
        //   width: 1000,
        //   margin: '0 auto',
        // }}
        className="edit-template-inner"
      >
        <Row>
          <Col>
            <DealershipChooser />
          </Col>
        </Row>
        <Row
          justify="space-between"
          align="middle"
          style={{ marginBottom: 24 }}
        >
          <Col>
            <Title level={3} editable={{ onChange: setFormTitle }}>
              {formTitle}
            </Title>
          </Col>
          <Col>
            <Space>
              {!editable && (
                <Button type="default" onClick={handleEdit}>
                  Edit
                </Button>
              )}
              <Button
                type="default"
                style={{
                  background: 'rgba(255, 77, 79, 0.2)',
                  borderColor: 'rgba(255, 77, 79, 0.0)',
                }}
                danger
                onClick={() =>
                  history.push('/inspection-forms', { tab: 'templates' })
                }
              >
                Cancel
              </Button>
              <Button
                type="primary"
                icon={<SaveOutlined />}
                onClick={handleSave}
                loading={loading}
                disabled={!editable}
              >
                Save & Exit
              </Button>
            </Space>
          </Col>
        </Row>

        <FleetDetails fleetLogoURL={fleetLogoURL} fleetAddress={fleetAddress} />

        <InitDetails />

        {groups.map((group, idx) => (
          <Group
            key={group.id}
            group={group}
            onDelete={() => deleteGroup(idx)}
            onDuplicate={() => duplicateGroup(idx)}
            onChange={(newGroup) => updateGroup(idx, newGroup)}
            editable={editable}
          />
        ))}
        <Button
          icon={<PlusOutlined />}
          type="dashed"
          block
          onClick={addGroup}
          style={{
            margin: '16px 0',
            height: 48,
            fontSize: 16,
            color: '#1677ff',
          }}
          disabled={!editable}
        >
          Add Group
        </Button>
      </div>
    </ResponsiveEditField>
  );
};

export default observer(InspectionForm);
